<script setup lang="ts">
import type { User as AuthUser } from '@models/auth/user';
import { computed, toRef } from 'vue';
import { UserStatus } from '@enums/userStatus';
import { type AccountManager } from '@models/accountManager';
import { type Author } from '@models/author';
import Typography from '@components/Typography.vue';
import ProfilePicture from '@components/ProfilePicture.vue';
import { useAuthor } from '@composables/useAuthor';
import { getShortReadableDateTime } from '@tools/date';
import Auth from '@tools/user/auth';
import { trans, transchoice } from '@elements/translation';

interface TimelineMessageProps {
  message: string,
  date: string | Date,
  author: Author | AccountManager,
}

const props = defineProps<TimelineMessageProps>();

const author = toRef(() => props.author);

const { pictureUrl, mediaUuid, isAccountManager } = useAuthor(author);

const currentUser = computed<AuthUser>(() => Auth.getUser()!);
const isOwner = computed<boolean>(() => currentUser.value.id === props.author.id);
const authorFirstname = computed<string>(() => (!isAccountManager.value && props.author.status !== UserStatus.ACTIVE ? trans('user.deleted.name') : props.author.firstname));
</script>

<template>
  <div
    class="timeline-message"
    :class="{ 'timeline-message--owner': isOwner }"
  >
    <ProfilePicture
      class="timeline-message__picture"
      :url="pictureUrl"
      :uuid="mediaUuid"
      :title="authorFirstname"
      :user-firstname="authorFirstname"
    />

    <div class="timeline-message__content-wrapper">
      <span
        v-if="!isOwner"
        class="timeline-message__tick"
      />

      <div
        class="timeline-message__content"
        :class="{ 'timeline-message__content--owner': isOwner }"
      >
        <!-- eslint-disable vue/no-v-html vue/no-v-text-v-html-on-component-->
        <Typography
          v-if="message"
          variant="body"
          class="timeline-message__body"
          v-html="message"
        />
        <!-- eslint-enable vue/no-v-html vue/no-v-text-v-html-on-component-->

        <div class="timeline-message__signature">
          <Typography variant="body-small-bold">
            {{
              isOwner ?
                trans('selection_item.comments_slider.timeline_message.signature.owner') :
                transchoice('selection_item.comments_slider.timeline_message.signature', { firstname: authorFirstname })
            }}
          </Typography>

          <Typography variant="body-small">
            {{ getShortReadableDateTime(date, ', ') }}
          </Typography>
        </div>
      </div>

      <span
        v-if="isOwner"
        class="timeline-message__tick timeline-message__tick--owner"
      />
    </div>
  </div>
</template>
